import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  cxmDepositReturnUrls,
  cxmService,
  CxmAction,
  CxmMethod,
  methodLimits,
  validateAmount,
} from "src/api";
import { useUrlParamsContext } from "src/contexts";
import { ERoutes } from "src/types";
import { formatNumberToExchange, onlyNumbers } from "src/utils";

/**
 * Decimals accepted in the amount field. A deposit is priced in the method's
 * own currency — fiat rails everywhere CXM operates — so this is not read from
 * the wallet, whose precision is the *crypto* side and would let a user type
 * eight decimals of ZAR.
 */
const AMOUNT_DECIMALS = 2;

/** What the gateway told us to do, once it is the user's problem rather than ours. */
export type CxmDepositOutcome =
  | { kind: "confirmed"; amount: string; status: string }
  | { kind: "unsupported"; action: CxmAction };

/**
 * CXM deposit orchestrator. Mirrors `useCxmWithdrawal`, minus the payout
 * account — that concept is withdrawal-only, so the flow is method → amount →
 * submit.
 *
 * The real difference is the tail: a payout ends when the backend accepts it,
 * whereas a deposit only starts there. The gateway answers with an `action`
 * and the customer still has to pay somewhere else, so this hook is
 * responsible for getting them there.
 */
export const useCxmDeposit = () => {
  const navigate = useNavigate();
  const { paymentRouteId } = useUrlParamsContext();

  const [methods, setMethods] = useState<CxmMethod[]>([]);
  const [methodsLoading, setMethodsLoading] = useState(true);
  const [methodsError, setMethodsError] = useState<string | null>(null);
  const [selectedMethodId, setSelectedMethodId] = useState<string | null>(null);

  const [amount, setAmount] = useState("");
  const [amountTouched, setAmountTouched] = useState(false);

  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<CxmDepositOutcome | null>(null);

  const selectedMethod = useMemo(
    () => methods.find((m) => m.methodId === selectedMethodId) ?? null,
    [methods, selectedMethodId]
  );

  /** An empty catalogue is a legitimate state: the operator granted no rules. */
  useEffect(() => {
    let cancelled = false;
    setMethodsLoading(true);
    cxmService
      .getMethods("deposit")
      .then((list) => {
        if (cancelled) return;
        setMethods(list);
        setSelectedMethodId(list[0]?.methodId ?? null);
        setMethodsError(null);
      })
      .catch((e: Error) => !cancelled && setMethodsError(e.message))
      .finally(() => !cancelled && setMethodsLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const currency = selectedMethod?.currency ?? "";

  const amountError = useMemo(
    () => (selectedMethod ? validateAmount(selectedMethod, amount || "0") : undefined) ?? undefined,
    [selectedMethod, amount]
  );

  const limits = useMemo(
    () => (selectedMethod ? methodLimits(selectedMethod) : { min: null, max: null }),
    [selectedMethod]
  );

  /** `onlyNumbers` is a predicate, not a formatter — it gates the keystroke. */
  const handleAmountChange = useCallback((value: string) => {
    setAmountTouched(true);
    if (!onlyNumbers(value)) return;
    setAmount(formatNumberToExchange(value, AMOUNT_DECIMALS));
  }, []);

  /**
   * Sends the customer to the hosted checkout through the widget's existing
   * `/payment-redirect` page, which nests the checkout in an inner iframe.
   *
   * `window.location` is deliberately not used: the widget is itself an
   * iframe on the tenant's site, so navigating it replaces the widget with
   * the checkout inside a frame the host sized for a form — and once there,
   * nothing can post the outcome back out. The nested-iframe route is what
   * the Alfred hosted rails already use, and it is what makes the return trip
   * work: the gateway's return lands on `/deposit-status/*` inside the inner
   * frame, which posts up to the widget frame, where the Router-level
   * `useReceiveDepositStatusRedirect` navigates the widget to the status page.
   */
  const handleAction = useCallback(
    (action: CxmAction, submittedAmount: string, status: string) => {
      if (action.type === "redirect" && action.url) {
        navigate(`${ERoutes.PaymentRedirect}?redirectUrl=${encodeURIComponent(action.url)}`);
        return;
      }
      if (action.type === "none") {
        setOutcome({ kind: "confirmed", amount: submittedAmount, status });
        return;
      }
      // `form_post` needs a self-submitting form POSTed into the checkout
      // frame. Nothing in the widget does that today, and guessing the target
      // frame and encoding would strand the money mid-flight, so the user is
      // told plainly instead of being shown a dead end.
      setOutcome({ kind: "unsupported", action });
    },
    [navigate]
  );

  const submitDisabled =
    submitLoading || !selectedMethod || !amount || Boolean(amountError) || !paymentRouteId;

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      if (!selectedMethod || !paymentRouteId) return;

      setSubmitLoading(true);
      setSubmitError(null);
      try {
        const res = await cxmService.initiateDeposit({
          payment_route_id: paymentRouteId,
          amount,
          methodId: selectedMethod.methodId,
          ...cxmDepositReturnUrls(),
        });
        handleAction(res.action, amount, res.status);
      } catch (e) {
        setSubmitError((e as Error).message);
      } finally {
        setSubmitLoading(false);
      }
    },
    [selectedMethod, paymentRouteId, amount, handleAction]
  );

  const reset = useCallback(() => {
    setOutcome(null);
    setAmount("");
    setAmountTouched(false);
    setSubmitError(null);
  }, []);

  return {
    currency,
    methods,
    methodsLoading,
    methodsError,
    selectedMethod,
    selectedMethodId,
    setSelectedMethodId,

    amount,
    amountTouched,
    amountError,
    limits,
    handleAmountChange,

    submitDisabled,
    submitLoading,
    submitError,
    outcome,
    handleSubmit,
    reset,
  };
};

/** The orchestrator's state, so the page can own the hook and pass it down. */
export type CxmDepositState = ReturnType<typeof useCxmDeposit>;
